import { useEffect, useMemo, useState } from "react";
import { PageHeader, PageBody } from "@/components/AppLayout";
import { api, downloadFile } from "@/lib/api";
import { fmtDate, fmtMoney, fmtNumber } from "@/lib/format";
import { useCompactMoney } from "@/lib/useCompactMoney";
import { useRowDensity } from "@/lib/useRowDensity";
import CompactMoneyToggle from "@/components/CompactMoneyToggle";
import RowDensityToggle from "@/components/RowDensityToggle";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  FileText, FileSpreadsheet, Wand2, CheckCircle2, AlertTriangle,
  Banknote, Filter, Columns2, Flag, X, Loader2, Lock, Pencil, Save,
  History, Lightbulb, Undo2,
} from "lucide-react";
import ReconciliationVerifyPanel from "@/components/reconciliation/ReconciliationVerifyPanel";
import ReconDateRange, { usToIso } from "@/components/reconciliation/ReconDateRange";
import ReconRangePresets from "@/components/reconciliation/ReconRangePresets";
import ReconSignOff from "@/components/reconciliation/ReconSignOff";
import StatementBalanceActions, { SourceTag } from "@/components/statements/StatementBalanceActions";
import { ReconChainWarnings } from "@/components/statements/StatementChain";

const rangeKey = (uid, accId) => `recon-range:${uid || "anon"}:${accId}`;
const readRange = (uid, accId) => {
  try { return JSON.parse(localStorage.getItem(rangeKey(uid, accId))) || { start: "", end: "" }; }
  catch { return { start: "", end: "" }; }
};

export default function Reconciliation() {
  const { user } = useAuth();
  const { money } = useCompactMoney();
  const { cfg: densityCfg } = useRowDensity();
  const [accounts, setAccounts] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);
  const [showOnlyMismatches, setShowOnlyMismatches] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [flagCount, setFlagCount] = useState(0);
  const [flagsOpen, setFlagsOpen] = useState(false);
  const [flags, setFlags] = useState([]);
  const [flagsLoading, setFlagsLoading] = useState(false);
  // Click-to-focus a comparison row — mirrors the "selected row" style on
  // the Transactions page so users can tap a line to pin it while cross-
  // checking against the statement PDF.
  const [focusedRowId, setFocusedRowId] = useState(null);
  const [startText, setStartText] = useState("");
  const [endText, setEndText] = useState("");
  const startIso = usToIso(startText);
  const endIso = usToIso(endText);
  const rangeError = startIso && endIso && startIso > endIso
    ? "Beginning date must be on or before ending date" : "";
  // Last range that is complete + valid for `acc`; partial typing keeps the previous one.
  const [range, setRange] = useState({ acc: "", start: "", end: "" });
  const [statements, setStatements] = useState([]);
  useEffect(() => {
    if (!selectedId) return;
    const saved = readRange(user?.id, selectedId);
    setStartText(saved.start || "");
    setEndText(saved.end || "");
    setRange({ acc: selectedId, start: usToIso(saved.start) || "", end: usToIso(saved.end) || "" });
    api.get("/statements", { params: { bank_account_id: selectedId, limit: 500 } })
      .then((r) => setStatements(r.data || []))
      .catch(() => setStatements([]));
    // eslint-disable-next-line
  }, [selectedId]);
  useEffect(() => {
    if (!selectedId || range.acc !== selectedId) return;
    if (startIso === null || endIso === null || rangeError) return;
    localStorage.setItem(rangeKey(user?.id, selectedId), JSON.stringify({ start: startText, end: endText }));
    setRange((r) => (r.start === startIso && r.end === endIso ? r : { acc: selectedId, start: startIso, end: endIso }));
    // eslint-disable-next-line
  }, [startIso, endIso, rangeError]);
  const pickRange = (a, b) => { setStartText(a); setEndText(b); };
  const rangeParams = () => ({
    ...(range.start ? { start: range.start } : {}),
    ...(range.end ? { end: range.end } : {}),
  });

  const loadAccounts = async () => {
    try {
      const r = await api.get("/bank-accounts");
      setAccounts(r.data);
      if (r.data.length && !selectedId) setSelectedId(r.data[0].id);
      if (!r.data.length) setLoading(false);
    } catch {}
  };

  const loadDetail = async (accId) => {
    if (!accId) { setDetail(null); return; }
    setLoading(true);
    try {
      const r = await api.get("/reports/reconciliation/detail", { params: { bank_account_id: accId, ...rangeParams() } });
      setDetail(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load reconciliation");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAccounts(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (range.acc && range.acc === selectedId) { loadDetail(selectedId); loadFlags(selectedId); }
    // eslint-disable-next-line
  }, [range]);

  // Esc clears the pinned row — power-user shortcut so you don't have to
  // click the row (or the chip) again to drop focus. Arrow Up / Down moves
  // the pin to the previous / next visible reconciliation row so an auditor
  // can tab through dozens of lines without touching the mouse.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { setFocusedRowId(null); return; }
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
      // Bail if the user is typing in an input / textarea / contenteditable.
      const tag = (e.target && e.target.tagName) || "";
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || e.target?.isContentEditable) return;
      if (!detail || !detail.lines || detail.lines.length === 0) return;
      e.preventDefault();
      const visible = detail.lines.filter((l) => {
        if (!showOnlyMismatches) return true;
        const hasStmt = l.statement_balance != null;
        return hasStmt && l.difference != null && Math.abs(l.difference) >= 0.01;
      });
      if (visible.length === 0) return;
      const idx = visible.findIndex((l) => l.id === focusedRowId);
      let next;
      if (idx === -1) {
        next = e.key === "ArrowDown" ? visible[0] : visible[visible.length - 1];
      } else {
        const step = e.key === "ArrowDown" ? 1 : -1;
        // Clamp at the ends rather than wrapping — auditors expect to stay on
        // the last/first row when they overshoot, not pop back to the start.
        next = visible[Math.max(0, Math.min(visible.length - 1, idx + step))];
      }
      setFocusedRowId(next.id);
      // Scroll the newly focused row into view.
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-testid="recon-row-${next.id}"]`);
        if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
      });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focusedRowId, detail, showOnlyMismatches]);

  const loadFlags = async (accId) => {
    if (!accId) { setFlags([]); setFlagCount(0); return; }
    setFlagsLoading(true);
    try {
      const r = await api.get("/transactions", {
        params: { bank_account_id: accId, flagged: true, limit: 500, ...rangeParams() },
      });
      setFlags(r.data || []);
      setFlagCount((r.data || []).length);
    } catch { /* non-fatal */ }
    finally { setFlagsLoading(false); }
  };

  const clearFlag = async (tx) => {
    try {
      await api.patch(`/transactions/${tx.id}`, { notes: "" });
      setFlags((arr) => arr.filter((x) => x.id !== tx.id));
      setFlagCount((n) => Math.max(0, n - 1));
      toast.success("Flag cleared");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to clear flag");
    }
  };

  // Freeze last month in one tap. Months later than that typically still
  // have adjustments landing, so the quick button defaults to the previous
  // calendar month; the full picker (year + month + note) lives in Settings.
  const closeCurrentMonth = async () => {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const label = d.toLocaleString(undefined, { month: "long", year: "numeric" });
    if (!window.confirm(`Close ${label}?\nTransactions dated in ${label} will become read-only. You can re-open from Settings.`)) return;
    try {
      await api.post("/locked-periods", { year, month, note: "Quick-closed from Reconciliation" });
      toast.success(`${label} is now closed`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Lock failed");
    }
  };

  const autoReconcile = async () => {
    if (!selectedId) return;
    setReconciling(true);
    try {
      const r = await api.post("/transactions/auto-reconcile", { bank_account_id: selectedId, ...rangeParams() });
      toast.success(`Auto-reconciled ${r.data.reconciled} of ${r.data.checked} transactions (${r.data.method.replace("-", " ")})`);
      await loadDetail(selectedId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Auto-reconcile failed");
    } finally { setReconciling(false); }
  };

  const exportFile = async (format) => {
    if (!selectedId) return;
    try {
      await downloadFile(
        `/reports/reconciliation/export?${new URLSearchParams({ bank_account_id: selectedId, format, ...rangeParams() })}`,
        `reconciliation_${detail?.account?.name || "report"}.${format}`,
      );
    } catch { toast.error("Export failed"); }
  };

  const lines = useMemo(() => {
    if (!detail?.lines) return [];
    if (!showOnlyMismatches) return detail.lines;
    return detail.lines.filter(
      (l) => l.difference !== null && l.difference !== undefined && Math.abs(l.difference) >= 0.01,
    );
  }, [detail, showOnlyMismatches]);

  const summary = detail?.summary;
  const hasStmt = summary?.has_statement_balances;
  const canEditStmt = ["owner", "admin", "accountant"].includes(user?.role);
  // Statement the closing-balance shortcut acts on: the one used, else the latest in range.
  const balanceStmt = useMemo(() => {
    const srcId = summary?.closing_statement_source?.statement_id;
    if (srcId) return statements.find((x) => x.id === srcId) || null;
    const inRange = statements.filter((x) => x.status === "parsed" && x.period_end
      && (!range.start || x.period_end >= range.start) && (!range.end || x.period_end <= range.end));
    return inRange.sort((a, b) => b.period_end.localeCompare(a.period_end))[0] || null;
  }, [summary, statements, range]);
  const asOf = summary?.closing_statement_source ? ` as of ${fmtDate(summary.closing_statement_source.period_end)}` : "";
  const closingMatches =
    summary?.closing_statement != null &&
    Math.abs((summary?.closing_difference) || 0) < 0.01;

  return (
    <>
      <PageHeader
        title="Reconciliation"
        subtitle="Statement vs system comparison"
        testid="reconciliation-header"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <CompactMoneyToggle testid="reconciliation-compact-toggle" />
            <RowDensityToggle testid="reconciliation-density" />
            <button
              onClick={() => setFlagsOpen(true)}
              disabled={!selectedId}
              data-testid="recon-flags-open"
              className="relative text-sm px-3 py-2 text-white hover:opacity-90 flex items-center gap-1.5 disabled:opacity-50 transition-opacity"
              style={{ background: "#D97706" }}
              title="Transactions you flagged for follow-up"
            >
              <Flag size={14} /> Follow-ups
              {flagCount > 0 && (
                <span
                  data-testid="recon-flags-badge"
                  className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-mono-fin bg-white"
                  style={{ color: "#D97706" }}
                >
                  {flagCount}
                </span>
              )}
            </button>
            <button
              onClick={closeCurrentMonth}
              disabled={!selectedId}
              data-testid="recon-close-month"
              className="text-sm px-3 py-2 text-white hover:opacity-90 flex items-center gap-1.5 disabled:opacity-50 transition-opacity"
              style={{ background: "#475569" }}
              title="Freeze last month to lock reconciled transactions"
            >
              <Lock size={14} /> Close month
            </button>
            <button
              onClick={() => setVerifyOpen(true)}
              disabled={!selectedId}
              data-testid="recon-verify-open"
              className="text-sm px-3 py-2 text-white hover:opacity-90 flex items-center gap-1.5 disabled:opacity-50 transition-opacity"
              style={{ background: "#0D9488" }}
              title="Side-by-side verify against the original statement file"
            >
              <Columns2 size={14} /> Verify side-by-side
            </button>
            <button
              onClick={autoReconcile}
              disabled={!selectedId || reconciling}
              data-testid="auto-reconcile-btn"
              className="text-sm px-4 py-2 text-white hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-1.5"
              style={{ background: "#1E40AF" }}
            >
              <Wand2 size={14} /> {reconciling ? "Reconciling…" : "Auto-reconcile"}
            </button>
            <button
              onClick={() => exportFile("pdf")}
              disabled={!selectedId}
              data-testid="reconciliation-pdf-btn"
              className="text-sm px-3 py-2 text-white hover:opacity-90 flex items-center gap-1.5 disabled:opacity-50 transition-opacity"
              style={{ background: "#DC2626" }}
            >
              <FileText size={14} /> PDF
            </button>
            <button
              onClick={() => exportFile("xlsx")}
              disabled={!selectedId}
              data-testid="reconciliation-xlsx-btn"
              className="text-sm px-3 py-2 text-white hover:opacity-90 flex items-center gap-1.5 disabled:opacity-50 transition-opacity"
              style={{ background: "#16A34A" }}
            >
              <FileSpreadsheet size={14} /> Excel
            </button>
          </div>
        }
      />
      <PageBody>
        {/* Account selector */}
        <div className="ll-card-flat p-4 mb-5 flex flex-wrap items-center gap-3" data-testid="reconciliation-account-selector">
          <Banknote size={16} className="opacity-60" />
          <label className="text-xs uppercase tracking-widest text-[var(--ll-ink-soft)]">Account</label>
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            data-testid="reconciliation-account-select"
            className="px-3 py-2 border bg-white text-sm min-w-[260px]"
            style={{ borderColor: "var(--ll-border)" }}
          >
            {accounts.length === 0 && <option value="">No accounts — add one first</option>}
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>{a.name} · {a.bank_name} ({a.account_number_masked})</option>
            ))}
          </select>
          <ReconDateRange
            start={startText}
            end={endText}
            onStart={setStartText}
            onEnd={setEndText}
            error={rangeError}
          />
          <ReconRangePresets onPick={pickRange} statements={statements} />
          {detail && (
            <label className="ml-auto flex items-center gap-2 text-sm cursor-pointer" data-testid="reconciliation-mismatch-toggle">
              <input
                type="checkbox"
                checked={showOnlyMismatches}
                onChange={(e) => setShowOnlyMismatches(e.target.checked)}
                className="accent-[var(--ll-ink)]"
              />
              <Filter size={13} /> Show only mismatches
            </label>
          )}
        </div>

        {loading && <div className="ll-progress mb-5" />}

        {!loading && accounts.length === 0 && (
          <div className="ll-card-flat p-12 text-center text-sm text-[var(--ll-ink-soft)]" data-testid="reconciliation-empty">
            No bank accounts yet. Add an account to start reconciling.
          </div>
        )}

        {!loading && detail && summary && (
          <>
            <ReconSignOff
              accountId={selectedId}
              range={range}
              summary={summary}
              canSign={["owner", "admin", "accountant"].includes(user?.role)}
              refreshKey={detail}
            />
            <ReconChainWarnings accountId={selectedId} range={range} refreshKey={statements} onJump={pickRange} />
            {/* Closing-balance match banner */}
            <div
              data-testid="reconciliation-banner"
              className={`mb-5 p-4 border flex items-center gap-3 ${
                summary.closing_statement == null
                  ? "bg-[var(--ll-mint)]"
                  : closingMatches
                  ? "bg-[var(--ll-mint)]"
                  : ""
              }`}
              style={{
                borderColor: summary.closing_statement != null && !closingMatches
                  ? "var(--ll-error)"
                  : "var(--ll-border)",
                background:
                  summary.closing_statement != null && !closingMatches
                    ? "#FEE2E2"
                    : undefined,
              }}
            >
              {summary.closing_statement == null ? (
                <>
                  <AlertTriangle size={18} className="text-[var(--ll-warning)] shrink-0" />
                  <div className="text-sm">
                    <span className="font-medium">No statement balance column.</span>{" "}
                    Closing computed from opening balance + transactions: <span className="font-mono-fin">{fmtMoney(summary.closing_computed)}</span>. Auto-reconcile will trust parsed entries.
                  </div>
                </>
              ) : closingMatches ? (
                <>
                  <CheckCircle2 size={18} className="text-[var(--ll-success)] shrink-0" />
                  <div className="text-sm">
                    <span className="font-medium">Closing balances match.</span>{" "}
                    Statement <span className="font-mono-fin">{fmtMoney(summary.closing_statement)}</span> = Computed <span className="font-mono-fin">{fmtMoney(summary.closing_compare_computed ?? summary.closing_computed)}</span>{asOf}.
                  </div>
                </>
              ) : (
                <>
                  <AlertTriangle size={18} className="text-[var(--ll-error)] shrink-0" />
                  <div className="text-sm">
                    <span className="font-medium text-[var(--ll-error)]">Closing balances differ by {fmtMoney(summary.closing_difference)}.</span>{" "}
                    Statement <span className="font-mono-fin">{fmtMoney(summary.closing_statement)}</span> vs Computed <span className="font-mono-fin">{fmtMoney(summary.closing_compare_computed ?? summary.closing_computed)}</span>{asOf}.
                  </div>
                </>
              )}
            </div>

            {/* Two-column summary: statement-side vs system-side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6" data-testid="reconciliation-summary">
              <div className="ll-card-flat p-6" data-testid="recon-statement-side">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] mb-2">Bank statement side</div>
                <div className="font-display font-bold text-xl mb-4">As reported by the bank</div>
                <EditableOpeningBalance
                  accountId={selectedId}
                  value={summary.opening_balance}
                  accountOpening={detail.account.opening_balance}
                  periodStart={detail.period_start}
                  canEdit={["owner", "admin", "accountant"].includes(user?.role)}
                  onSaved={() => loadDetail(selectedId)}
                />
                <Row
                  label="Statement balances parsed"
                  value={
                    <span data-testid="recon-stmt-balance-count">
                      {fmtNumber(summary.statement_balance_count)} rows · {fmtNumber(summary.statement_total_count || 0)} statement ending
                    </span>
                  }
                />
                <Row
                  label={summary.closing_statement_source
                    ? `Closing balance (statement, ${fmtDate(summary.closing_statement_source.period_end)})`
                    : "Closing balance (statement)"}
                  value={<span data-testid="recon-closing-statement">{summary.closing_statement != null ? money(summary.closing_statement) : "Not in statement"}</span>}
                  emphasis
                />
                {(summary.closing_statement_source || balanceStmt) && (
                  <div className="flex items-center justify-end gap-2 text-[11px] text-[var(--ll-ink-soft)] pt-2" data-testid="recon-closing-statement-source">
                    {summary.closing_statement_source ? (
                      <span>
                        From {summary.closing_statement_source.file_name}
                        <SourceTag source={summary.closing_statement_source.balance_source} testid="recon-closing-source-tag" />
                      </span>
                    ) : (
                      <span>Latest statement: {balanceStmt.file_name}</span>
                    )}
                    {balanceStmt && canEditStmt && (
                      <StatementBalanceActions
                        statement={balanceStmt}
                        testidPrefix="recon"
                        onUpdated={(u) => {
                          setStatements((arr) => arr.map((x) => (x.id === u.id ? u : x)));
                          loadDetail(selectedId);
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
              <div className="ll-card-flat p-6" data-testid="recon-system-side">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] mb-2">Financials Ready side</div>
                <div className="font-display font-bold text-xl mb-4">Computed from transactions</div>
                <Row label="Total credits" value={<span className="text-[var(--ll-success)]">{money(summary.total_credits)}</span>} />
                <Row label="Total debits" value={<span className="text-[var(--ll-error)]">{money(summary.total_debits)}</span>} />
                <Row label="Net change" value={<span data-testid="recon-net-change">{money(summary.net_change)}</span>} />
                <Row label="Transactions" value={fmtNumber(summary.transaction_count)} />
                <Row label="Reconciled" value={
                  <span>
                    {fmtNumber(summary.matched_count)}{" "}
                    <span className="text-[var(--ll-ink-muted)] text-xs">/ {fmtNumber(summary.transaction_count)}</span>
                  </span>
                } />
                <Row label="Closing balance (computed)" value={money(summary.closing_computed)} emphasis />
              </div>
            </div>

            {/* Side-by-side comparison table */}
            <div className="ll-card-flat overflow-x-auto" data-testid="reconciliation-table-wrap">
              <div className="px-5 py-3 border-b flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: "var(--ll-border)" }}>
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="font-display font-bold text-lg">Transaction comparison</div>
                  {/* Legend — decodes the colors / check in the Match column at a glance */}
                  <div className="hidden sm:flex items-center gap-3 text-[10px] uppercase tracking-widest text-[var(--ll-ink-soft)]" data-testid="reconciliation-legend">
                    <span className="inline-flex items-center gap-1.5">
                      <CheckCircle2 size={12} className="text-[var(--ll-success)]" /> Match
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="font-mono-fin text-[var(--ll-error)]">−$</span>
                      Mismatch (red difference)
                    </span>
                    <span className="inline-flex items-center gap-1.5" title="Arrow Up / Down to move pin · Esc to clear">
                      <span className="inline-block w-3 h-3" style={{ background: "#0b5d9e" }} />
                      Hover / active <span className="font-mono-fin text-[10px] opacity-70">↑↓</span>
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-[var(--ll-ink-soft)]">
                  {focusedRowId && (
                    <button
                      type="button"
                      onClick={() => setFocusedRowId(null)}
                      data-testid="recon-clear-focus"
                      title="Clear focused row (Esc)"
                      className="inline-flex items-center gap-1 px-2 py-1 border bg-[#0b5d9e] text-white hover:bg-[#0a4f87] transition-colors"
                      style={{ borderColor: "#0b5d9e" }}
                    >
                      <X size={11} /> Clear focus
                      <span className="hidden md:inline opacity-70 ml-1 font-mono-fin text-[9px]">ESC</span>
                    </button>
                  )}
                  <span>{lines.length} of {summary.transaction_count} {showOnlyMismatches && "(mismatches only)"}</span>
                </div>
              </div>
              <table className={`w-full ${densityCfg.txt} recon-cmp`} data-testid="reconciliation-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-white border-b" style={{ background: "#0F1E3C", borderColor: "var(--ll-border)" }}>
                    <th className={`px-4 ${densityCfg.tdY} font-medium`}>Date</th>
                    <th className={`px-4 ${densityCfg.tdY} font-medium`}>Description</th>
                    <th className={`px-4 ${densityCfg.tdY} font-medium text-right`}>Amount</th>
                    {hasStmt && <th className={`px-4 ${densityCfg.tdY} font-medium text-right`}>Statement Bal.</th>}
                    <th className={`px-4 ${densityCfg.tdY} font-medium text-right`}>Computed Bal.</th>
                    {hasStmt && <th className={`px-4 ${densityCfg.tdY} font-medium text-right`}>Difference</th>}
                    <th className={`px-4 ${densityCfg.tdY} font-medium text-center`}>Match</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 && (
                    <tr><td colSpan={hasStmt ? 7 : 5} className="px-4 py-10 text-center text-sm text-[var(--ll-ink-soft)]" data-testid="reconciliation-no-rows">
                      {showOnlyMismatches ? "Nothing to investigate — every statement balance matches." : "No transactions for this account yet."}
                    </td></tr>
                  )}
                  {lines.map((l) => {
                    const isMismatch =
                      hasStmt && l.difference !== null && l.difference !== undefined && Math.abs(l.difference) >= 0.01;
                    const isFocused = focusedRowId === l.id;
                    const baseCls = isFocused
                      ? "bg-[#0b5d9e] text-white hover:bg-[#0a4f87] cursor-pointer"
                      : "hover:bg-[#0b5d9e] hover:text-white cursor-pointer";
                    return (
                      <tr
                        key={l.id}
                        data-testid={`recon-row-${l.id}`}
                        data-focused={isFocused || undefined}
                        data-mismatch={isMismatch || undefined}
                        onClick={() => setFocusedRowId(isFocused ? null : l.id)}
                        className={`border-b transition-colors ${baseCls}`}
                        style={{ borderColor: "var(--ll-border)" }}
                      >
                        <td className={`px-4 ${densityCfg.tdY} font-mono-fin text-xs whitespace-nowrap`}>{fmtDate(l.date)}</td>
                        <td className={`px-4 ${densityCfg.tdY} max-w-md truncate`} title={l.description}>{l.description}</td>
                        <td className={`px-4 ${densityCfg.tdY} font-mono-fin text-right ${l.amount < 0 ? "text-[var(--ll-error)]" : "text-[var(--ll-success)]"}`}>{fmtMoney(l.amount)}</td>
                        {hasStmt && (
                          <td className={`px-4 ${densityCfg.tdY} font-mono-fin text-right`}>
                            {l.statement_balance != null ? fmtMoney(l.statement_balance) : <span className="text-[var(--ll-ink-muted)]">—</span>}
                          </td>
                        )}
                        <td className={`px-4 ${densityCfg.tdY} font-mono-fin text-right`}>{fmtMoney(l.computed_balance)}</td>
                        {hasStmt && (
                          <td className={`px-4 ${densityCfg.tdY} font-mono-fin text-right ${isMismatch ? "text-[var(--ll-error)] font-medium" : "text-[var(--ll-ink-muted)]"}`}>
                            {l.difference != null ? fmtMoney(l.difference) : "—"}
                          </td>
                        )}
                        <td className={`px-4 ${densityCfg.tdY} text-center`}>
                          {l.matched || l.is_reconciled ? (
                            <CheckCircle2 size={15} className="inline text-[var(--ll-success)]" />
                          ) : (
                            <span className="inline-block w-3 h-3 border" style={{ borderColor: "var(--ll-border)" }} />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </PageBody>
      {verifyOpen && (
        <ReconciliationVerifyPanel
          bankAccountId={selectedId}
          start={range.start}
          end={range.end}
          onClose={() => { setVerifyOpen(false); loadDetail(selectedId); loadFlags(selectedId); }}
        />
      )}
      {flagsOpen && (
        <FlagsDrawer
          flags={flags}
          loading={flagsLoading}
          start={range.start}
          end={range.end}
          onClose={() => setFlagsOpen(false)}
          onClear={clearFlag}
        />
      )}
    </>
  );
}

function FlagsDrawer({ flags, loading, start, end, onClose, onClear }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex justify-end"
      onClick={onClose}
      data-testid="recon-flags-drawer"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white h-full w-full max-w-md shadow-xl overflow-hidden flex flex-col"
      >
        <div
          className="px-5 py-3 border-b flex items-center justify-between bg-[var(--ll-ink)] text-white"
          style={{ borderColor: "var(--ll-border)" }}
        >
          <div className="flex items-center gap-2">
            <Flag size={14} />
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-75">Follow-ups</div>
              <div className="font-display font-bold text-sm">{flags.length} flagged transaction{flags.length === 1 ? "" : "s"}</div>
              {(start || end) && (
                <div className="text-[10px] opacity-75 font-mono-fin" data-testid="recon-flags-range">
                  {start ? fmtDate(start) : "Beginning"} – {end ? fmtDate(end) : "Present"}
                </div>
              )}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="recon-flags-close" className="text-white/80 hover:text-white">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-10 text-center text-sm text-[var(--ll-ink-soft)]">
              <Loader2 className="mx-auto animate-spin mb-2" size={16} /> Loading…
            </div>
          ) : flags.length === 0 ? (
            <div className="p-10 text-center text-sm text-[var(--ll-ink-soft)]" data-testid="recon-flags-empty">
              <Flag size={24} className="mx-auto mb-2 opacity-40" />
              Nothing is flagged for follow-up on this account{start || end ? " in this date range" : ""}.
            </div>
          ) : (
            <ul className="divide-y" style={{ borderColor: "var(--ll-border)" }}>
              {flags.map((t) => (
                <li key={t.id} className="p-4 hover:bg-[#E0F2FE]" data-testid={`recon-flag-row-${t.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs font-mono-fin text-[var(--ll-ink-soft)]">
                        {fmtDate(t.date)}
                        <span className={`ml-3 ${t.amount < 0 ? "text-[var(--ll-error)]" : "text-[var(--ll-success)]"}`}>
                          {fmtMoney(t.amount)}
                        </span>
                      </div>
                      <div className="text-sm font-medium mt-1 truncate" title={t.description}>{t.description}</div>
                      <div className="mt-2 text-xs p-2 border-l-2 border-[var(--ll-warning)] bg-[var(--ll-ochre)]/10">
                        <Flag size={10} className="inline mr-1 text-[var(--ll-warning)]" />
                        {t.notes}
                      </div>
                    </div>
                    <button
                      onClick={() => onClear(t)}
                      data-testid={`recon-flag-clear-${t.id}`}
                      className="shrink-0 text-[10px] uppercase tracking-widest px-2 py-1 border hover:bg-[var(--ll-mint)]"
                      style={{ borderColor: "var(--ll-border)" }}
                      title="Clear this flag"
                    >
                      Resolve
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value, emphasis }) => (
  <div className="flex items-center justify-between py-2 border-b last:border-b-0" style={{ borderColor: "var(--ll-border)" }}>
    <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">{label}</div>
    <div className={emphasis ? "font-mono-fin text-xl font-medium" : "font-mono-fin text-sm"}>{value}</div>
  </div>
);

/**
 * Opening balance row with:
 *   • pencil → inline input (editable only for owner/admin/accountant)
 *   • "History" link → side-drawer with every opening-balance mutation
 *   • Lightbulb icon → auto-fills the input with the statement-side
 *     suggestion pulled from the earliest parsed statement
 *
 * All mutations go through PATCH /bank-accounts/{id}; the server records
 * an audit row on every value change.
 */
function EditableOpeningBalance({ accountId, value, accountOpening, periodStart, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? 0));
  const [busy, setBusy] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const startEdit = async () => {
    setDraft(accountOpening == null ? "0" : String(accountOpening));
    setEditing(true);
    // Fetch the auto-suggestion lazily so the API cost only lands when the
    // user actually wants to change the balance.
    try {
      const r = await api.get(`/bank-accounts/${accountId}/opening-balance-suggestion`);
      setSuggestion(r.data);
    } catch { setSuggestion(null); /* no statements yet — fine */ }
  };

  const applySuggestion = () => {
    if (suggestion?.suggested != null) {
      setDraft(String(suggestion.suggested));
      toast.success(`Suggested from ${suggestion.source_file_name || "earliest statement"}`);
    }
  };

  const save = async () => {
    const n = parseFloat(String(draft).replace(/,/g, ""));
    if (!Number.isFinite(n)) { toast.error("Enter a number"); return; }
    setBusy(true);
    try {
      await api.patch(`/bank-accounts/${accountId}`, { opening_balance: n });
      toast.success("Opening balance updated");
      setEditing(false);
      setSuggestion(null);
      onSaved && onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const r = await api.get(`/bank-accounts/${accountId}/opening-balance-history`);
      setHistory(r.data.items || []);
    } catch (err) {
      toast.error("Failed to load history");
    } finally { setHistoryLoading(false); }
  };

  if (!editing) {
    return (
      <>
        <div className="flex items-center justify-between py-2 border-b" style={{ borderColor: "var(--ll-border)" }} data-testid="recon-opening-row">
          <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">{periodStart ? `Opening balance (${fmtDate(periodStart)})` : "Opening balance"}</div>
          <div className="flex items-center gap-2">
            <div className="font-mono-fin text-sm" data-testid="recon-opening-value">{fmtMoney(value)}</div>
            <button
              onClick={openHistory}
              data-testid="recon-opening-history"
              className="p-1 border hover:bg-[var(--ll-mint)]"
              style={{ borderColor: "var(--ll-border)" }}
              title="View change history"
              aria-label="View opening balance history"
            >
              <History size={11} />
            </button>
            {canEdit && (
              <button
                onClick={startEdit}
                data-testid="recon-opening-edit"
                className="p-1 border hover:bg-[var(--ll-mint)]"
                style={{ borderColor: "var(--ll-border)" }}
                title="Edit opening balance"
                aria-label="Edit opening balance"
              >
                <Pencil size={11} />
              </button>
            )}
          </div>
        </div>
        {historyOpen && (
          <OpeningBalanceHistoryDrawer
            items={history}
            loading={historyLoading}
            currentValue={accountOpening}
            accountId={accountId}
            canEdit={canEdit}
            onReverted={() => {
              setHistoryOpen(false);
              onSaved && onSaved();
            }}
            onClose={() => setHistoryOpen(false)}
          />
        )}
      </>
    );
  }
  return (
    <div className="py-2 border-b" style={{ borderColor: "var(--ll-border)" }}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Opening balance</div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            step="0.01"
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
            data-testid="recon-opening-input"
            className="w-32 px-2 py-1 border font-mono-fin text-sm text-right"
            style={{ borderColor: "var(--ll-border)" }}
          />
          <button
            onClick={save}
            disabled={busy}
            data-testid="recon-opening-save"
            className="p-1 bg-[var(--ll-ink)] text-white hover:bg-[var(--ll-ink-hover)] disabled:opacity-50"
            title="Save"
            aria-label="Save opening balance"
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
          </button>
          <button
            onClick={() => setEditing(false)}
            data-testid="recon-opening-cancel"
            className="p-1 border hover:bg-[#E0F2FE]"
            style={{ borderColor: "var(--ll-border)" }}
            title="Cancel"
            aria-label="Cancel"
          >
            <X size={11} />
          </button>
        </div>
      </div>
      {suggestion?.suggested != null && Number(draft) !== Number(suggestion.suggested) && (
        <button
          type="button"
          onClick={applySuggestion}
          data-testid="recon-opening-suggestion"
          className="mt-2 ml-auto flex items-center gap-1 text-[11px] px-2 py-1 border bg-[var(--ll-mint)]/40 hover:bg-[var(--ll-mint)] w-full justify-between"
          style={{ borderColor: "var(--ll-border)" }}
          title={`Suggested from ${suggestion.source_file_name || "the earliest statement"} (${suggestion.period_start || "unknown period"})`}
        >
          <span className="flex items-center gap-1">
            <Lightbulb size={11} className="text-[var(--ll-ochre-ink)]" />
            Suggested from statement
          </span>
          <span className="font-mono-fin">{fmtMoney(suggestion.suggested)}</span>
        </button>
      )}
    </div>
  );
}

function OpeningBalanceHistoryDrawer({ items, loading, currentValue, accountId, canEdit, onReverted, onClose }) {
  const [revertingId, setRevertingId] = useState(null);

  const revert = async (row) => {
    const prettyPrev = fmtMoney(row.previous_value);
    const prettyCurrent = fmtMoney(currentValue);
    const msg = `Revert opening balance from ${prettyCurrent} back to ${prettyPrev}?\n\nThis writes a new history row so the audit trail stays complete.`;
    if (!window.confirm(msg)) return;
    setRevertingId(row.id);
    try {
      await api.patch(`/bank-accounts/${accountId}`, { opening_balance: row.previous_value });
      toast.success(`Reverted to ${prettyPrev}`);
      onReverted && onReverted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revert failed");
    } finally {
      setRevertingId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex justify-end"
      onClick={onClose}
      data-testid="opening-history-drawer"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white h-full w-full max-w-md shadow-xl overflow-hidden flex flex-col"
      >
        <div
          className="px-5 py-3 border-b flex items-center justify-between bg-[var(--ll-ink)] text-white"
          style={{ borderColor: "var(--ll-border)" }}
        >
          <div className="flex items-center gap-2">
            <History size={14} />
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-75">Audit</div>
              <div className="font-display font-bold text-sm">Opening balance history</div>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-white/80 hover:text-white">
            <X size={16} />
          </button>
        </div>
        <div className="px-5 py-3 border-b text-xs text-[var(--ll-ink-soft)] flex items-center justify-between" style={{ borderColor: "var(--ll-border)" }}>
          <span>Current</span>
          <span className="font-mono-fin text-sm text-[var(--ll-ink)]">{fmtMoney(currentValue)}</span>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-10 text-center text-sm text-[var(--ll-ink-soft)]">
              <Loader2 className="mx-auto animate-spin mb-2" size={16} /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-sm text-[var(--ll-ink-soft)]" data-testid="opening-history-empty">
              <History size={24} className="mx-auto mb-2 opacity-40" />
              No changes recorded yet.
            </div>
          ) : (
            <ul className="divide-y" style={{ borderColor: "var(--ll-border)" }}>
              {items.map((h) => {
                const alreadyAtPrev = Number(currentValue) === Number(h.previous_value);
                return (
                  <li key={h.id} className="p-4" data-testid={`opening-history-row-${h.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-xs text-[var(--ll-ink-soft)]">{fmtDate(h.changed_at)}</div>
                        <div className="text-sm font-medium mt-1">
                          <span className="line-through text-[var(--ll-ink-muted)] font-mono-fin">
                            {fmtMoney(h.previous_value)}
                          </span>
                          <span className="mx-2 text-[var(--ll-ink-muted)]">→</span>
                          <span className="font-mono-fin">{fmtMoney(h.new_value)}</span>
                        </div>
                        <div className="text-xs text-[var(--ll-ink-soft)] mt-1">by {h.actor_name}</div>
                      </div>
                      <span
                        className="font-mono-fin text-xs shrink-0 px-2 py-0.5"
                        style={{
                          background: h.delta >= 0 ? "#DCFCE7" : "#FEE2E2",
                          color: h.delta >= 0 ? "#166534" : "#991B1B",
                        }}
                      >
                        {h.delta >= 0 ? "+" : ""}{fmtMoney(h.delta)}
                      </span>
                    </div>
                    {canEdit && (
                      <div className="mt-2.5 flex items-center justify-end">
                        <button
                          type="button"
                          onClick={() => revert(h)}
                          disabled={revertingId === h.id || alreadyAtPrev}
                          data-testid={`opening-history-revert-${h.id}`}
                          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest px-2 py-1 border hover:bg-[var(--ll-mint)] disabled:opacity-40 disabled:cursor-not-allowed"
                          style={{ borderColor: "var(--ll-border)" }}
                          title={alreadyAtPrev
                            ? `Already at ${fmtMoney(h.previous_value)}`
                            : `Set opening balance back to ${fmtMoney(h.previous_value)}`}
                        >
                          {revertingId === h.id
                            ? <Loader2 size={11} className="animate-spin" />
                            : <Undo2 size={11} />}
                          Revert to {fmtMoney(h.previous_value)}
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
